import ITalent from "../../../../core/interfaces/ITalent.ts";
import { useIntl } from "react-intl";
import { useCallback, useEffect, useState } from "react";
import getProfilePicture from "../../../../core/utils/getProfilePicture.ts";
import { KTSVG } from "../../../../../_metronic/helpers/index.ts";
import convertToFraction from "../../../../core/utils/convertToFraction.ts";

export interface IComponent {
    show: boolean
    onClose: () => void
    selectedTalent: ITalent | null
    onDownloadPDF?: () => void
}

export default function (props: IComponent) {
    const intl = useIntl()

    const [slides, setSlides] = useState<any[]>([])
    const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768)
    const [showMobileInfo, setShowMobileInfo] = useState<boolean>(true)

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const createSlides = useCallback((files: any[]) => {
        if (isMobile) {
            setSlides(files.map((file) => [file]));
        } else {
            const filesPairs = files.reduce((pairs, file, index) => {
                if (index % 2 === 0) {
                    pairs.push([file]);
                } else {
                    pairs[pairs.length - 1].push(file);
                }
                return pairs;
            }, []);
            setSlides(filesPairs);
        }
    }, [isMobile])

    useEffect(() => {
        if (props.selectedTalent?.images && props.selectedTalent.images.length > 0) {
            createSlides(props.selectedTalent.images)
        } else if (props.selectedTalent?.talent?.media?.files) {
            createSlides(props.selectedTalent.talent.media.files)
        }
    }, [props.selectedTalent, createSlides]);

    useEffect(() => {
        if (props.show) {
            document.body.classList.add('modal-open')
        } else {
            document.body.classList.remove('modal-open')
        }
        return () => {
            document.body.classList.remove('modal-open')
        }
    }, [props.show]);

    const { measurements, characteristics } = props.selectedTalent?.talent?.physical || {}

    if (!props.show) return null

    return <>
        <div className="modal-backdrop fade show" onClick={props.onClose}></div>
        <div
            className={`modal fade show`}
            style={{ display: 'block' }}
            id="kt_modal_add_event"
            aria-hidden="false"
            data-bs-focus="false"
            onClick={(e) => e.target === e.currentTarget && props.onClose()}
        >
            <div className="modal-dialog modal-fullscreen">
                <div className="modal-content">
                    <div className="modal-header">
                        <div className="talent-nav">
                            <h1 className="text-capitalize fw-normal">{props.selectedTalent?.fullName || `${props.selectedTalent?.name?.split(' ')[0]} ${props.selectedTalent?.lastName?.split(' ')[0]}`}</h1>
                        </div>
                        {props.onDownloadPDF &&
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                aria-label="Download PDF"
                                onClick={props.onDownloadPDF}
                            >
                                <i className="las la-download fs-2"></i>
                            </div>
                        }
                        <div
                            className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                            aria-label="Close"
                            onClick={props.onClose}
                        >
                            <KTSVG
                                path="media/icons/duotune/arrows/arr061.svg"
                                className="svg-icon svg-icon-2x"
                            />
                        </div>
                    </div>
                    <div className="modal-body p-0">
                        <div id="kt_carousel_talent"
                            className="carousel slide carousel-fade"
                            data-bs-ride="false"
                            data-bs-interval="8000"
                        >
                            <div className="carousel-inner">
                                {/* Desktop: first slide with info + profile photo side by side */}
                                {!isMobile &&
                                    <div className="carousel-item active">
                                        <div className="row align-items-stretch h-100">
                                            <div className="col align-self-center">
                                                <div className="talent-info text-center">
                                                    <h1 className="text-capitalize fw-normal mb-10">{props.selectedTalent?.fullName || `${props.selectedTalent?.name?.split(' ')[0]} ${props.selectedTalent?.lastName?.split(' ')[0]}`}</h1>
                                                    <div className="talent-measurements">
                                                        {measurements &&
                                                            Object.entries(measurements).map(([key, value]) => (
                                                                <div className="detail-element" key={key}>
                                                                    <span className="detail-title text-capitalize">{key}</span>
                                                                    <span className="detail-value">{value}cm / {convertToFraction(value / 2.54)}</span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                    {characteristics &&
                                                        <div className="talent-characteristics">
                                                            {(characteristics.hairColor || characteristics.hairLength || characteristics.hairType) &&
                                                                <div className="detail-element">
                                                                    <span className="detail-title text-capitalize">Hair</span>
                                                                    <span className="detail-value text-capitalize">{characteristics.hairType ?? ''} {characteristics.hairLength ?? ''} {characteristics.hairColor ?? ''}</span>
                                                                </div>
                                                            }

                                                            {(characteristics.dressSize) &&
                                                                <div className="detail-element">
                                                                    <span className="detail-title text-capitalize">Dress Size</span>
                                                                    <span className="detail-value text-capitalize">{characteristics.dressSize ?? ''} {characteristics.dressSizeMeasurementUnit ?? ''}</span>
                                                                </div>
                                                            }

                                                            {(characteristics.shoesSize) &&
                                                                <div className="detail-element">
                                                                    <span className="detail-title text-capitalize">Shoes Size</span>
                                                                    <span className="detail-value text-capitalize">{characteristics.shoesSize ?? ''} {characteristics.shoesSizeMeasurementUnit ?? ''}</span>
                                                                </div>
                                                            }
                                                        </div>
                                                    }
                                                    <div className="talent-social-media">
                                                        {props.selectedTalent?.talent?.socialMedia?.socialMedia &&
                                                            <div className="social-media-element my-3">
                                                                <a className="btn btn-link btn-color-gray-800 btn-active-color-primary" href={`https://www.instagram.com/${props.selectedTalent?.talent?.socialMedia?.socialMedia[0].userName}`}>
                                                                    <span className="social-icon fa-brands fa-instagram fs-2x"></span>
                                                                    <span className="social-username ms-2">@{props.selectedTalent?.talent?.socialMedia?.socialMedia[0].userName}</span>
                                                                </a>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col">
                                                {props.selectedTalent &&
                                                    <img className={'talent-picture'} src={getProfilePicture(props?.selectedTalent)} />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                }

                                {/* Mobile: profile photo slide with glassmorphism overlay */}
                                {isMobile && props.selectedTalent &&
                                    <div className="carousel-item active">
                                        <div className="row align-items-stretch h-100 gx-0">
                                            <div className="col position-relative">
                                                <img className={'talent-picture'} src={getProfilePicture(props?.selectedTalent)} />
                                                <div className={`mobile-talent-overlay ${showMobileInfo ? 'expanded' : 'collapsed'}`} onClick={() => setShowMobileInfo(!showMobileInfo)}>
                                                    <div className="overlay-handle"></div>
                                                    <h2 className="text-capitalize fw-semibold mb-0">{props.selectedTalent?.fullName || `${props.selectedTalent?.name?.split(' ')[0]} ${props.selectedTalent?.lastName?.split(' ')[0]}`}</h2>
                                                    {showMobileInfo && <>
                                                        <div className="talent-measurements mt-2">
                                                            {measurements &&
                                                                Object.entries(measurements).map(([key, value]) => (
                                                                    <div className="detail-element" key={key}>
                                                                        <span className="detail-title text-capitalize">{key}</span>
                                                                        <span className="detail-value">{value}cm / {convertToFraction(value / 2.54)}</span>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                        {characteristics &&
                                                            <div className="talent-characteristics mt-1">
                                                                {(characteristics.hairColor || characteristics.hairLength || characteristics.hairType) &&
                                                                    <div className="detail-element">
                                                                        <span className="detail-title text-capitalize">Hair</span>
                                                                        <span className="detail-value text-capitalize">{characteristics.hairType ?? ''} {characteristics.hairLength ?? ''} {characteristics.hairColor ?? ''}</span>
                                                                    </div>
                                                                }
                                                                {(characteristics.dressSize) &&
                                                                    <div className="detail-element">
                                                                        <span className="detail-title text-capitalize">Dress Size</span>
                                                                        <span className="detail-value text-capitalize">{characteristics.dressSize ?? ''} {characteristics.dressSizeMeasurementUnit ?? ''}</span>
                                                                    </div>
                                                                }
                                                                {(characteristics.shoesSize) &&
                                                                    <div className="detail-element">
                                                                        <span className="detail-title text-capitalize">Shoes Size</span>
                                                                        <span className="detail-value text-capitalize">{characteristics.shoesSize ?? ''} {characteristics.shoesSizeMeasurementUnit ?? ''}</span>
                                                                    </div>
                                                                }
                                                            </div>
                                                        }
                                                        {props.selectedTalent?.talent?.socialMedia?.socialMedia &&
                                                            <div className="mt-2">
                                                                <a className="text-white text-decoration-none" href={`https://www.instagram.com/${props.selectedTalent?.talent?.socialMedia?.socialMedia[0].userName}`}>
                                                                    <span className="fa-brands fa-instagram me-1"></span>
                                                                    @{props.selectedTalent?.talent?.socialMedia?.socialMedia[0].userName}
                                                                </a>
                                                            </div>
                                                        }
                                                    </>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                }

                                {slides.length > 0 && slides.map((slideFiles, index) =>
                                    <div className={`carousel-item`} key={slideFiles[0].id || index}>
                                        <div className="row align-items-stretch h-100 gx-0">
                                            <div className="col">
                                                <img className={'talent-picture'} src={slideFiles[0].url || slideFiles[0]} />
                                            </div>
                                            {slideFiles[1] &&
                                                <div className="col">
                                                    <img className={'talent-picture'} src={slideFiles[1].url || slideFiles[1]} />
                                                </div>
                                            }
                                        </div>
                                    </div>
                                )}
                            </div>
                            {slides.length > 0 &&
                                <>
                                    <a className="carousel-control-prev" href="#kt_carousel_talent" role="button" data-bs-slide="prev">
                                        <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                                        <span className="sr-only">Previous</span>
                                    </a>
                                    <a className="carousel-control-next" href="#kt_carousel_talent" role="button" data-bs-slide="next">
                                        <span className="carousel-control-next-icon" aria-hidden="true"></span>
                                        <span className="sr-only">Next</span>
                                    </a>
                                </>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
}
